import { useEffect, useRef, useState, type FormEvent } from 'react';
import { BrandCard } from './buds-header';
import { callApi, ShopifyAppApiError } from '@/lib/api';
import { errorInlineMessage } from './api-errors';
import type { StatusView } from './connection-root';

interface Props {
  shop: string;
  initialStatus: StatusView;
  onChange: (status: StatusView) => void;
}

interface LinkResponse {
  status: 'CONNECTED';
  shopifyShopDomain: string;
  shopifyShopId: string;
  storeId: string;
  partnerId: string;
  storeName: string;
  partnerName: string;
  installedAt: string;
  autoSyncEnabled: boolean;
  initialSyncCompletedAt: string | null;
  lastAutoSyncedAt: string | null;
  lastAutoSyncError: string | null;
  syncScope: 'ALL' | 'SELECTED';
  syncDefaults: NonNullable<StatusView['syncDefaults']>;
}

export function ConnectionForm({ shop, initialStatus, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [editing, setEditing] = useState(
    initialStatus.status === 'NOT_CONFIGURED',
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [confirmCountdown, setConfirmCountdown] = useState(0);

  // Hold the confirm button disabled briefly so the warning is read before the
  // destructive disconnect becomes clickable.
  useEffect(() => {
    if (!confirmingDisconnect || confirmCountdown <= 0) return;
    const timer = setTimeout(() => setConfirmCountdown((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [confirmingDisconnect, confirmCountdown]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const apiKey = inputRef.current?.value.trim();
    if (!apiKey) {
      setError('Paste your Buds API key first.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await callApi<LinkResponse>('/v1/shopify-app/link', {
        method: 'POST',
        extraHeaders: { 'x-api-key': apiKey },
        body: {},
      });
      onChange({
        status: 'CONNECTED',
        shopifyShopDomain: res.shopifyShopDomain,
        shopifyShopId: res.shopifyShopId,
        storeId: res.storeId,
        partnerId: res.partnerId,
        autoSyncEnabled: res.autoSyncEnabled,
        initialSyncCompletedAt: res.initialSyncCompletedAt,
        lastAutoSyncedAt: res.lastAutoSyncedAt,
        lastAutoSyncError: res.lastAutoSyncError,
        syncScope: res.syncScope,
        syncDefaults: res.syncDefaults,
        storeName: res.storeName,
        partnerName: res.partnerName,
      });
      setEditing(false);
    } catch (err) {
      const e = err as ShopifyAppApiError;
      if (e.code === 'INVALID_KEY') {
        setError('That API key is invalid or revoked.');
      } else if (e.code === 'CONFLICT') {
        setError('This Shopify shop is already linked to a different store.');
      } else {
        setError(errorInlineMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onDisconnect = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await callApi('/v1/shopify-app/link', { method: 'DELETE' });
      onChange({ status: 'NOT_CONFIGURED', shopifyShopDomain: shop });
      setConfirmingDisconnect(false);
      setEditing(true);
    } catch (err) {
      setError(errorInlineMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (initialStatus.status === 'CONNECTED' && !editing) {
    return (
      <BrandCard title={`Connected to ${initialStatus.storeName ?? '—'}`}>
        <p className="text-sm leading-relaxed text-buds-dark/80">
          Partner: {initialStatus.partnerName ?? '—'}
        </p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="rounded-full border border-buds-dark/20 bg-white px-4 py-2 text-sm font-medium text-buds-dark disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => setEditing(true)}
            disabled={submitting || confirmingDisconnect}
          >
            Change API key
          </button>
          {!confirmingDisconnect && (
            <button
              type="button"
              className="rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => {
                setError(null);
                setConfirmCountdown(5);
                setConfirmingDisconnect(true);
              }}
              disabled={submitting}
            >
              Disconnect
            </button>
          )}
        </div>
        {confirmingDisconnect && (
          <div className="mt-3 rounded-md border border-red-300/60 bg-red-50 px-3 py-3 text-xs text-red-900">
            <p className="font-medium">Disconnect this shop from Buds?</p>
            <p className="mt-1">
              Buds will stop fulfilling redemptions for this shop, and the
              rewards synced from it will be removed from your loyalty program.
              Reconnecting and running a sync again restores them.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={onDisconnect}
                disabled={submitting || confirmCountdown > 0}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-60"
              >
                {submitting
                  ? 'Disconnecting…'
                  : confirmCountdown > 0
                    ? `Yes, disconnect (${confirmCountdown})`
                    : 'Yes, disconnect'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDisconnect(false)}
                disabled={submitting}
                className="rounded-md border border-red-700/30 bg-white px-3 py-1.5 text-xs font-medium text-red-900 hover:bg-red-100 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {error ? <p className="text-sm text-red-600 mt-2">{error}</p> : null}
      </BrandCard>
    );
  }

  return (
    <BrandCard title="Connect this shop to Buds">
      <form onSubmit={onSubmit} className="space-y-3">
        {initialStatus.message ? (
          <div className="rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {initialStatus.message}
          </div>
        ) : null}
        <p className="text-sm">
          Paste the Buds API key from <strong>{shop}</strong>&#39;s Buds partner
          dashboard.
        </p>
        <input
          ref={inputRef}
          type="password"
          name="apiKey"
          autoComplete="off"
          className="w-full rounded border border-buds-dark/20 px-3 py-2"
          placeholder="bud_..."
        />
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        <div className="flex gap-2">
          <button
            type="submit"
            className="rounded-full bg-buds-dark px-4 py-2 text-sm font-semibold text-white"
            disabled={submitting}
          >
            {submitting ? 'Connecting…' : 'Connect'}
          </button>
          {initialStatus.status === 'CONNECTED' ? (
            <button
              type="button"
              className="rounded-full border border-buds-dark/20 bg-white px-4 py-2 text-sm font-medium text-buds-dark"
              onClick={() => setEditing(false)}
              disabled={submitting}
            >
              Cancel
            </button>
          ) : null}
        </div>
      </form>
    </BrandCard>
  );
}
